import type { EntriesQueryVariables, EntryInterfaceTypeFragment } from '#graphql-operations'
import { useInfiniteQuery } from '@tanstack/vue-query'

interface MinimalFilter {
  id: string | number
  title: string
  slug: string
}
type FiltersCategory = (EntryInterfaceTypeFragment | MinimalFilter)[]
interface UseArchiveOptions {
  section: string[]
  pageSize?: number
  queryKey?: string
  filtersByDate?: boolean
  filtersSection?: string[]
  filtersQueryParam?: string
  filtersCategory?: FiltersCategory
  enabledFilters?: string[]
  initialFilters?: string[]
  relatedToEntries?: EntriesQueryVariables['relatedToEntries']
  initialEntries?: any[]
  orderBy?: EntriesQueryVariables['orderBy']
}

export async function useArchive<TEntry = unknown>(options: UseArchiveOptions) {
  const hasInitialEntries = Boolean(options.initialEntries?.length)
  const route = useRoute()
  const filtersQueryParam = options.filtersQueryParam || 'category'
  const routeQuery = useRouteQuery(filtersQueryParam)

  const routeFilters = computed(() => {
    const value = route.query[filtersQueryParam]
    if (!value)
      return []

    return (Array.isArray(value) ? value.filter(Boolean) : [value]) as string[]
  })

  const normalizedEnabledFilters = computed(() => {
    if (!options.enabledFilters?.length)
      return undefined
    return options.enabledFilters.filter(Boolean)
  })

  const filtersCategory = ref<FiltersCategory>(options.filtersCategory ?? [])
  if (options.filtersByDate) {
    const currentYear = new Date().getFullYear()
    filtersCategory.value = Array.from({ length: 5 }, (_, i) => ({
      id: currentYear - i,
      title: String(currentYear - i),
      slug: String(currentYear - i),
    }))
  }

  if (options.filtersSection?.length && !filtersCategory.value?.length) {
    console.warn('[useArchive] - Missing filters data')
  }

  const sanitizeFilters = (value: string[]) => {
    if (!normalizedEnabledFilters.value?.length)
      return value

    return value.filter(filter => normalizedEnabledFilters.value?.includes(filter))
  }

  const filters = ref<string[]>(
    sanitizeFilters(options.initialFilters?.length ? options.initialFilters : routeFilters.value),
  )
  const hasActiveFilters = computed(() => filters.value.length > 0)
  const shouldUseInitialEntries = computed(() => hasInitialEntries && !hasActiveFilters.value)

  if (routeQuery.value !== filters.value) {
    routeQuery.value = filters.value
  }

  watch(filters, (value) => {
    routeQuery.value = sanitizeFilters(value)
  })

  const pageSize = options.pageSize ?? 24

  const variables = computed<EntriesQueryVariables>(() => {
    const vars = {
      section: options.section,
      relatedToEntries: options.relatedToEntries,
      orderBy: options.orderBy,
    } as EntriesQueryVariables

    if (options.filtersByDate && filters.value.length) {
      const ranges = filters.value.map(year => ['and', `>= ${year}`, `< ${Number(year) + 1}`])
      vars.date = ranges.length === 1 ? ranges[0] : ['and', ...ranges]
      vars.date = vars.date!.flat()
    }
    else if (!options.filtersByDate && options.filtersSection?.length && filtersCategory.value.length && filters.value.length) {
      vars.relatedToEntries = [
        ...(vars.relatedToEntries || []),
        { section: options.filtersSection, slug: filters.value },
      ]
    }

    return vars
  })

  const key = options.queryKey || `${options.section.join('-')}-archive`
  const queryKey = computed(() => [key, variables.value])

  const {
    status,
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    suspense,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ctx => fetchServerPage<TEntry>(pageSize, ctx.pageParam as number, variables.value),
    getNextPageParam: lastGroup => lastGroup.nextOffset,
    initialPageParam: 0,
    enabled: computed(() => !shouldUseInitialEntries.value),
  })

  if (!shouldUseInitialEntries.value) {
    await suspense()
  }

  const count = computed(() => {
    if (shouldUseInitialEntries.value) {
      return options.initialEntries?.length ?? 0
    }
    return data.value?.pages[0]?.count ?? 0
  })

  const entries = computed(() => {
    return shouldUseInitialEntries.value
      ? (options.initialEntries ?? [])
      : (data.value ? data.value.pages.flatMap(page => page.rows) : [])
  })

  const statusComputed = computed(() => (shouldUseInitialEntries.value ? 'success' : status.value))
  const errorComputed = computed(() => (shouldUseInitialEntries.value ? undefined : error.value))
  const isFetchingNextPageComputed = computed(() => (shouldUseInitialEntries.value ? undefined : isFetchingNextPage.value))
  const fetchNextPageComputed = computed(() => (shouldUseInitialEntries.value ? undefined : fetchNextPage))

  const hasNextPageComputed = computed(() => {
    if (shouldUseInitialEntries.value) {
      return undefined
    }
    return hasNextPage.value
  })

  return {
    filtersCategory,
    filters,
    count,
    entries,
    status: statusComputed,
    error: errorComputed,
    isFetchingNextPage: isFetchingNextPageComputed,
    fetchNextPage: fetchNextPageComputed,
    hasNextPage: hasNextPageComputed,
  }
}
